import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { bs } from '../../../styles/styles';
import { InputFieldError } from '../../../components/InputFieldError';
import { AutomationStepCardTextingInput } from '../../../components/automation/AutomationStepCard/presentation';
import { styleSheet as cardStyleSheet } from '../../../components/automation/AutomationStepCard/styles';

interface TextingStepFormProps {
	step: Api.ITextingAutomationStep | null;
	trigger: Api.IAutomationTrigger | null;
	schedule: Api.IAutomationStepSchedule | null;
	readonly: boolean;
	showRequiredFieldError: boolean;
	/** Template ID for conditional step mutations and attachment API calls */
	templateId?: string;
	/** Step ID for conditional step mutations and attachment API calls */
	stepId?: string;
	/** Impersonation context for admin users editing customer automations */
	impersonationContext?: Api.IImpersonationContext;
	onSaveStep: (
		stepToSave?: Partial<Api.ITextingAutomationStep>,
		newSchedule?: Api.IAutomationStepSchedule
	) => Promise<void>;
	onClearError: () => void;
	/** Callback when a conditional step is added/removed, to refresh template data */
	onStepChanged?: () => void;
	/** Callback to update the step data locally after a mutation (e.g. conditional step added/removed) */
	updateStep?: (stepId: string, updater: (prev: Api.IAutomationStep) => Api.IAutomationStep) => void;
}

/**
 * Form component for editing texting automation step content.
 * Displays a text editor for composing SMS/text messages with merge fields.
 */
export function TextingStepForm({
	step,
	trigger,
	schedule,
	readonly,
	showRequiredFieldError,
	templateId,
	stepId,
	impersonationContext,
	onSaveStep,
	onClearError,
	onStepChanged,
	updateStep,
}: TextingStepFormProps) {
	const hasTextContent = !!step?.content;

	const errorMessage = React.useMemo(() => {
		if (showRequiredFieldError && !hasTextContent) {
			return 'Please provide text content.';
		}
		return null;
	}, [showRequiredFieldError, hasTextContent]);

	if (!step) {
		return <div>Loading step data...</div>;
	}

	return (
		<>
			<div className={css(cardStyleSheet.fieldLabel)}>
				Text content: &nbsp;
				<span className={css(bs.required)}>*</span>
			</div>
			<InputFieldError errorMessage={errorMessage}>
				<AutomationStepCardTextingInput
					index={0}
					onRequestClearErrorMessages={onClearError}
					onRequestSave={onSaveStep}
					onStepChanged={onStepChanged}
					updateStep={updateStep}
					schedule={schedule ?? undefined}
					step={step}
					trigger={trigger ?? undefined}
					readOnly={readonly}
					templateId={templateId}
					stepId={stepId}
					impersonationContext={impersonationContext}
				/>
			</InputFieldError>
		</>
	);
}
